<template>
  <div class="animated fadeIn">
    <hm-tour :tour="tourSteps" tourFlag="TourWelcome"></hm-tour>

    <b-row v-if="ready && licenseDaysRemaining < 30">
      <b-col lg="12">
        <hm-alert />
        <b-card header="License Expiring Soon" class="card-accent-danger">
          <div>
            {{ licenseDaysRemaining }} days remaining on your legacy HIPAAMATE
            subscription. The legacy software product cannot be renewed;
            therefore, please begin using the NEW version of HIPAAMATE.
          </div>
          <div>
            <u>Important</u>: Because the legacy product cannot be renewed be
            sure to print your policies as well as any risk analysis and risk
            management reports BEFORE your subscription expires.
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12">
        <hm-alert />
        <b-card header="News and Updates" class="card-accent-info">
          <div>
            <b>Important!</b> You are currently using the <u>legacy</u> version
            of HIPAAMATE. This software will no longer be available after
            currently active legacy subscriptions expire.
          </div>
          <div>
            New HIPAAMATE is better in every way! Please send us a Support
            message (above) if you're interested in switching (no cost) before
            your plan expires.
          </div>
        </b-card>
      </b-col>
    </b-row>

    <hm-spinner v-if="!ready" />

    <b-row v-show="ready">
      <b-col sm="12" xl="6">
        <b-card no-body header="Items" class="card-accent-success">
          <b-list-group>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/workforce-list"
            >
              <span> <i class="icon-people pr-2"></i> Workforce </span>
              <b-badge variant="primary" pill>{{
                dashboard.workforce.count
              }}</b-badge>
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/inventory-list"
            >
              <span> <i class="icon-screen-desktop pr-2"></i> Inventory </span>
              <b-badge variant="primary" pill>{{
                dashboard.inventory.count
              }}</b-badge>
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/ba-list"
            >
              <span>
                <i class="icon-notebook pr-2"></i> Business Associates
              </span>
              <b-badge variant="primary" pill>{{ dashboard.ba.count }}</b-badge>
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/practice/logbook"
            >
              <span> <i class="icon-note pr-2"></i> Maintenance Logbook </span>
              <b-badge variant="primary" pill>{{
                dashboard.logbook.count
              }}</b-badge>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <b-col sm="12" xl="6">
        <b-card no-body header="Tasks" class="card-accent-danger">
          <b-list-group>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/training-docs"
            >
              <span> <i class="icon-graduation pr-2"></i> Staff Training </span>
              <b-badge
                :variant="pillStyleCount(dashboard.workforce.trainingDue)"
                pill
                >{{ dashboard.workforce.trainingDue }}</b-badge
              >
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/policy-list"
            >
              <span> <i class="icon-book-open pr-2"></i> Policies </span>
              <b-badge
                :variant="pillStyleDays(dashboard.policy.approved)"
                pill
                >{{ pillDays(dashboard.policy.approved) }}</b-badge
              >
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/activity-list"
            >
              <span> <i class="icon-eye pr-2"></i> Activity Review </span>
              <b-badge :variant="pillStyleDays(dashboard.activity.date)" pill>{{
                pillDays(dashboard.activity.date)
              }}</b-badge>
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/risk"
            >
              <span> <i class="icon-shield pr-2"></i> Risk Management </span>
              <b-badge :variant="pillStyleDays(dashboard.rm.completed)" pill>{{
                pillDays(dashboard.rm.completed)
              }}</b-badge>
            </b-list-group-item>
            <b-list-group-item
              class="d-flex justify-content-between align-items-center"
              to="/task-list"
            >
              <span> <i class="icon-note pr-2"></i> Mitigation Tasks </span>
              <b-badge
                :variant="pillStyleDaysUntil(dashboard.task.date)"
                pill
                >{{ dashboard.task.count }}</b-badge
              >
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { practiceService } from "@/services";

export default {
  name: "Dashboard",
  data() {
    return {
      dashboard: {
        workforce: {
          count: 0,
          trainingDue: 0
        },
        inventory: {
          count: 0
        },
        ba: {
          count: 0
        },
        logbook: {
          count: 0
        },
        policy: {
          approved: ""
        },
        activity: {
          date: ""
        },
        rm: {
          completed: ""
        },
        task: {
          count: 0,
          date: ""
        }
      },
      tourSteps: [
        {
          target: "navbar-brand",
          placement: "bottom",
          title: "Welcome to HIPAAMATE!",
          content:
            "<p>Let's take a quick tour of the application to get you started.</p><p>You can click on this logo at any time to return to the dashboard.</p>"
        },
        {
          target: "navbar-support",
          placement: "bottom",
          icon: "icon-support",
          title: "Support",
          content:
            "<p>If you need help, please send us your question and we will get right back to you.</p>"
        },
        {
          target: "sidebar-nav",
          placement: "right",
          icon: "icon-menu",
          title: "Getting Started",
          content:
            "<p>To begin, work your way down the navigation menu, beginning with the items under Practice. Enter all applicable information before moving to the next item.</p><p>It is that simple!</p>"
        },
        {
          target: "sidebar-nav",
          placement: "righttop",
          title: "Tour Complete",
          content:
            "Thank you for choosing HIPAAMATE! We look forward to being your compliance partner."
        }
      ],
      ready: false
    };
  },
  computed: {
    ...mapState("user", {
      licenseDaysRemaining(state) {
        return state.license && state.license.remaining
          ? state.license.remaining
          : 365;
      }
    })
  },
  methods: {
    ...mapActions({
      alertException: "alert/exception"
    }),
    pillStyleCount(n) {
      return n > 0 ? "danger" : "success";
    },
    pillStyleDays(d) {
      let days = this.$options.filters.daysUntilDue(d, 365);
      if (days > 30) return "success";
      if (days > 0) return "warning";
      return "danger";
    },
    pillStyleDaysUntil(d) {
      if (!d) return "success";
      let days = this.$options.filters.daysUntil(d);
      if (days > 30) return "success";
      if (days > 0) return "warning";
      return "danger";
    },
    pillDays(d) {
      if (!d || d.length < 1) return "To-do";
      let days = this.$options.filters.daysUntilDue(d, 365);
      let adays = Math.abs(days);
      let pluralText = adays !== 1 ? "s" : "";
      let dueText = days > 0 ? " until due" : " past due";
      return days !== 0 ? adays + " day" + pluralText + dueText : "Due today";
    },
    pillDaysUntil(d) {
      let days = this.$options.filters.daysUntil(d);
      return days > 0 ? days + "days" : "Past Due";
    }
  },
  created() {
    this.ready = false;

    this.$store.dispatch("user/fetchLicense"); // fetch license information

    practiceService
      .dashboard()
      .then(data => {
        this.dashboard = data;
      })
      .catch(err => {
        this.alertException({ err, message: "Error loading dashboard!" });
      })
      .finally(() => {
        this.ready = true;
      });
  }
};
</script>
